import { useEffect, useState } from 'react';

import { ListItemText, Menu, MenuItem } from '@mui/material';

import FilterButton from './FilterButton';
import { useFilterAnchor } from './useFilterAnchor';

import { useGetAllMediaPlansQuery } from '@/api/endpoints/mediaPlan';
import { FILTER_DEBOUNCE_TIME_MS } from '@/app/constants';
import { Checkbox } from '@/shared/components/checkbox';
import { useGlobalFilterKeys } from '@/shared/hooks';

interface MediaPlanFilterProps {
  filterId: string;
  buttonAriaLabel: string;
  searchParamMediaPlanId: number | undefined;
  handleNavigation: (mediaPlanId: number | undefined) => void;
}

const MediaPlanFilter = ({
  filterId,
  buttonAriaLabel,
  searchParamMediaPlanId,
  handleNavigation,
}: MediaPlanFilterProps) => {
  const { anchorEl, handleOpen, handleClose, open, popoverId } =
    useFilterAnchor(filterId);

  const { globalFilterKeys, isGlobalFilterReady } = useGlobalFilterKeys();
  const { data: mediaPlans = [], isLoading } = useGetAllMediaPlansQuery(
    globalFilterKeys,
    { skip: !isGlobalFilterReady }
  );

  const [selectedId, setSelectedId] = useState<number | undefined>(
    searchParamMediaPlanId
  );
  const [isChangedInternally, setIsChangedInternally] = useState(false);

  useEffect(() => {
    setSelectedId(searchParamMediaPlanId);
    setIsChangedInternally(false);
  }, [searchParamMediaPlanId]);

  useEffect(() => {
    if (!isChangedInternally) {
      return;
    }

    const timer = setTimeout(() => {
      setIsChangedInternally(false);
      handleNavigation(selectedId);
    }, FILTER_DEBOUNCE_TIME_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [handleNavigation, isChangedInternally, selectedId]);

  const handleSelect = (id: number) => {
    setIsChangedInternally(true);
    setSelectedId((prev) => (prev === id ? undefined : id));
  };

  return (
    <>
      <FilterButton
        isActive={selectedId !== undefined}
        onClick={handleOpen}
        aria-label={buttonAriaLabel}
        loading={isLoading}
      />
      <Menu
        id={popoverId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {mediaPlans.length ? (
          mediaPlans.map((plan) => (
            <MenuItem
              key={plan.id}
              onClick={() => {
                handleSelect(plan.id);
              }}
              disableGutters
              sx={{ py: 1.25, pr: 1.75, pl: 1 }}
            >
              <Checkbox
                id={plan.id.toString()}
                checked={selectedId === plan.id}
              />
              <ListItemText
                primary={plan.name}
                slotProps={{
                  primary: { variant: 'body3', color: 'textTertiary' },
                }}
              />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemText
              primary='No media plans available'
              slotProps={{ primary: { variant: 'body2' } }}
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default MediaPlanFilter;
